import { useCallback, useMemo, useState } from "react";
import { useGetQueryDateParametersQuery } from "~/graphql/supergraphHooks";
import {
  hasAllDateParameterValues,
  toPreviewParameters,
  type DateParameterValues,
} from "./derive";
import type { DateParameter } from "~/graphql/supergraphTypes";

const NO_PARAMETERS: readonly DateParameter[] = [];

const LOAD_ERROR_MESSAGE =
  "We couldn't load the dates this template needs. Refresh the page to try again.";

type UsePreviewDateParametersArgs = {
  /** Query the template renders against. Null until it resolves; no inputs are required. */
  queryId: number | null;
  arenaId: number | null;
};

/**
 * Collects a concrete date for every `DateParameter` the template's query
 * requires, and packages them for the preview mutation's `parameters` argument.
 *
 * The required set comes from the backend rather than being derived from the
 * query's date periods: it also covers the token named by a `DYNAMIC_DATE`
 * field, which no date period mentions, and execution rejects the preview if
 * any of them is missing.
 *
 * `isResolved` is false while the requirements are still loading so callers can
 * hold the preview back — firing early would either waste a render or fail
 * downstream with a missing-parameter error. A failed fetch also stays
 * unresolved, so callers must render `loadError` to avoid sitting on a spinner
 * that will never resolve.
 */
export const usePreviewDateParameters = ({
  queryId,
  arenaId,
}: UsePreviewDateParametersArgs) => {
  const { data, loading, error } = useGetQueryDateParametersQuery({
    variables: { queryId: queryId ?? 0, arenaId: arenaId ?? undefined },
    skip: queryId == null,
    fetchPolicy: "cache-and-network",
  });

  // Intentionally not cleared when the query changes: a date picked for
  // `PERIOD_END` still means the same thing to the next query that asks for it,
  // and re-entering dates after every data package change is worse than
  // carrying them over. `toPreviewParameters` drops anything the current query
  // doesn't ask for, so a stale value can never reach the mutation.
  const [values, setValues] = useState<DateParameterValues>({});

  const requiredParameters = data?.finpubQueryDateParameters?.parameters ?? NO_PARAMETERS;

  const setValue = useCallback((parameter: DateParameter, value: string | null) => {
    setValues((previous) => ({ ...previous, [parameter]: value }));
  }, []);

  const parameters = useMemo(
    () => toPreviewParameters(requiredParameters, values),
    [requiredParameters, values],
  );

  return {
    requiredParameters,
    values,
    setValue,
    parameters,
    isComplete: hasAllDateParameterValues(requiredParameters, values),
    // `cache-and-network` keeps `loading` true during background refetches, so
    // treat already-delivered data as resolved rather than re-gating the preview.
    // A failed fetch stays unresolved: firing without the requirements would
    // render a preview the executor is going to reject anyway.
    isResolved: queryId == null || Boolean(data) || (!loading && !error),
    /** Copy for the caller's error state. Null while a cached set is usable. */
    loadError: error && !data ? LOAD_ERROR_MESSAGE : null,
  };
};
